/**
 * NMS Content Video Block
 * Extends NMSBaseClass following NMS Component Instance Architecture
 *
 * Features:
 * - Picks the mobile or desktop thumbnail (same 767px breakpoint as the mobile <source>)
 * - Tap-to-play: tapping the thumbnail plays the video muted
 */

const MOBILE_QUERY = '(max-width: 767px)';

class NMSContentVideoBlock extends window.NMSBaseClass {
  #isInitialized = false;
  #eventController = new AbortController();
  #nativeControls = false;

  constructor(element) {
    super();
    this.element = element;
    this.blockId = element.id;

    window.neemans = window.neemans || {};
    window.neemans.contentVideoBlock = window.neemans.contentVideoBlock || {};
    window.neemans.contentVideoBlock[this.blockId] = this;
  }

  init() {
    if (this.#isInitialized) return;

    super.init();
    if (!this.refs.video) return;

    this.#setPoster();
    if (this.refs.tapPlay) this.#setupTapToPlay();
    this.#isInitialized = true;
  }

  // The source is chosen once at load, so the poster is too (no swap on resize)
  #setPoster() {
    const { posterMobile, posterDesktop } = this.refs.video.dataset;
    const poster = window.matchMedia(MOBILE_QUERY).matches ? posterMobile : posterDesktop;
    if (poster) this.refs.video.poster = poster;
  }

  #setupTapToPlay() {
    const { video, tapPlay } = this.refs;
    const { signal } = this.#eventController;

    // Native controls come back after the first tap, if the merchant enabled them
    this.#nativeControls = video.controls;
    video.controls = false;
    tapPlay.hidden = false;

    video.addEventListener('ended', () => this.#showTapPlay(), { signal });

    if (!this.#nativeControls) {
      // No controls to pause with, so a tap on the playing video pauses it
      video.addEventListener('click', () => video.pause(), { signal });
      video.addEventListener('pause', () => this.#showTapPlay(), { signal });
    }
  }

  #showTapPlay() {
    this.refs.tapPlay.hidden = false;
  }

  handleTapPlay() {
    const { video, tapPlay } = this.refs;

    video.muted = true;
    video.controls = this.#nativeControls;
    tapPlay.hidden = true;

    video.play().catch(() => this.#showTapPlay());
  }

  destroy() {
    this.#eventController.abort();
    delete window.neemans.contentVideoBlock?.[this.blockId];
    super.destroy();
    this.#isInitialized = false;
  }
}

function initNMSContentVideoBlock() {
  if (!window.NMSBaseClass) {
    console.error('NMSContentVideoBlock: NMSBaseClass required');
    return;
  }

  for (const element of document.querySelectorAll('[data-nms-content-video]')) {
    if (!element.hasAttribute('data-content-video-initialized')) {
      new NMSContentVideoBlock(element).init();
      element.setAttribute('data-content-video-initialized', 'true');
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSContentVideoBlock);
} else {
  initNMSContentVideoBlock();
}

// Handle theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-content-video]')) {
    initNMSContentVideoBlock();
  }
});

window.neemans = window.neemans || {};
window.neemans.NMSContentVideoBlock = NMSContentVideoBlock;
